import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksAPI, uploadFile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Bug, Sparkles, Paperclip, Trash2, CheckCircle2, LogOut, Send, Loader2, AlertCircle, Clock, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const STATUS_COLORS = {
  todo: '#64748B',
  in_progress: '#F59E0B',
  testing: '#8B5CF6',
  done: '#10B981',
};

const PRIORITY_LABELS_RU = { high: 'Высокий', medium: 'Средний', low: 'Низкий' };

function TypeOption({ selected, value, label, description, Icon, color, onClick }) {
  const isSelected = selected === value;
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-2 rounded-xl p-5 text-left transition-all flex items-start gap-4 hover:shadow-md w-full"
      style={{
        borderColor: isSelected ? color : '#E0E0E0',
        backgroundColor: isSelected ? `${color}0D` : '#FFFFFF',
      }}
      data-testid={`type-option-${value}`}
    >
      <div
        className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}20`, color }}
      >
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-[#111111]" style={{ color: isSelected ? color : '#111111' }}>{label}</p>
        <p className="text-xs text-[#666666] mt-0.5">{description}</p>
      </div>
    </button>
  );
}

export default function RestaurantSubmitPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form' | 'submitting' | 'duplicates' | 'success'
  const [taskType, setTaskType] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [submittedTask, setSubmittedTask] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [uploadedIds, setUploadedIds] = useState([]);

  const handleFileSelect = (fileList) => {
    if (!fileList) return;
    const arr = Array.from(fileList);
    const valid = [];
    arr.forEach(f => {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} — файл слишком большой (макс 10MB)`);
        return;
      }
      valid.push(f);
    });
    if (valid.length) setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const doSubmit = async (attachmentIds, force) => {
    const submitRes = await tasksAPI.restaurantSubmit({
      task_type: taskType,
      description: description.trim(),
      attachment_ids: attachmentIds,
      force,
    });
    return submitRes.data;
  };

  const handleSubmit = async () => {
    if (!taskType) {
      toast.error('Выберите тип обращения');
      return;
    }
    if (!description.trim() || description.trim().length < 5) {
      toast.error('Пожалуйста, опишите проблему подробнее (мин. 5 символов)');
      return;
    }
    setStep('submitting');
    try {
      // 1. Upload attachments once (kept in state to reuse if user chooses "submit anyway")
      let attachmentIds = uploadedIds;
      if (attachmentIds.length === 0 && files.length > 0) {
        attachmentIds = [];
        for (const f of files) {
          try {
            const res = await uploadFile(f);
            if (res.data?.id) attachmentIds.push(res.data.id);
          } catch (err) {
            toast.error(`Не удалось загрузить ${f.name}`);
          }
        }
        setUploadedIds(attachmentIds);
      }
      // 2. Submit (AI analyzes + deduplicates)
      const data = await doSubmit(attachmentIds, false);
      if (data.status === 'duplicates_found') {
        setDuplicates(data.similar || []);
        setStep('duplicates');
      } else {
        setSubmittedTask(data);
        setStep('success');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось отправить обращение');
      setStep('form');
    }
  };

  const handleForceSubmit = async () => {
    setStep('submitting');
    try {
      const data = await doSubmit(uploadedIds, true);
      setSubmittedTask(data);
      setStep('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось отправить обращение');
      setStep('duplicates');
    }
  };

  const resetForm = () => {
    setTaskType('');
    setDescription('');
    setFiles([]);
    setUploadedIds([]);
    setSubmittedTask(null);
    setDuplicates([]);
    setStep('form');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8F3] via-[#FFFFFF] to-[#FFF3EF]">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E0E0E0]">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#101010] text-[#CFFF40] flex items-center justify-center font-bold text-sm">M</div>
            <div>
              <p className="text-sm font-semibold text-[#111111]">Mayo Portal</p>
              <p className="text-xs text-[#666666]">Добро пожаловать, {user?.name}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="restaurant-logout-btn">
            <LogOut size={14} className="mr-1" /> Выйти
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="restaurant-submit-page">
        {step === 'form' && (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-[#111111]">Расскажите нам</h1>
              <p className="text-base text-[#666666]">Ответьте на 3 коротких вопроса — команда получит уведомление сразу</p>
            </div>

            {/* Q1 - Type */}
            <div className="bg-white rounded-2xl border border-[#E0E0E0] p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35] text-white flex items-center justify-center text-sm font-bold">1</div>
                <h2 className="text-lg font-semibold text-[#111111]">Это баг или идея к улучшению?</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TypeOption
                  selected={taskType}
                  value="bug"
                  label="Это баг"
                  description="Что-то работает не так, ошибка или сбой"
                  Icon={Bug}
                  color="#DC2626"
                  onClick={() => setTaskType('bug')}
                />
                <TypeOption
                  selected={taskType}
                  value="feature"
                  label="Идея к улучшению"
                  description="Хочу предложить новую фичу или улучшение"
                  Icon={Sparkles}
                  color="#8B5CF6"
                  onClick={() => setTaskType('feature')}
                />
              </div>
            </div>

            {/* Q2 - Description */}
            <div className="bg-white rounded-2xl border border-[#E0E0E0] p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35] text-white flex items-center justify-center text-sm font-bold">2</div>
                <h2 className="text-lg font-semibold text-[#111111]">Опишите подробнее</h2>
              </div>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Расскажите своими словами что произошло или что вы хотели бы изменить..."
                rows={6}
                className="text-base"
                data-testid="submit-description"
              />
              <p className="text-xs text-[#999999]">ИИ ассистент автоматически разберёт описание, определит модуль и приоритет.</p>
            </div>

            {/* Q3 - Files */}
            <div className="bg-white rounded-2xl border border-[#E0E0E0] p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35] text-white flex items-center justify-center text-sm font-bold">3</div>
                <h2 className="text-lg font-semibold text-[#111111]">Добавьте фото / видео / файлы <span className="text-sm text-[#999999] font-normal">(необязательно)</span></h2>
              </div>
              {files.length > 0 && (
                <div className="space-y-2">
                  {files.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-[#F5F5F5] rounded-lg px-3 py-2" data-testid={`file-item-${idx}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip size={14} className="text-[#666666] flex-shrink-0" />
                        <span className="text-sm truncate">{f.name}</span>
                        <span className="text-xs text-[#999999] flex-shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <button onClick={() => removeFile(idx)} className="text-[#999999] hover:text-[#DC2626]" data-testid={`file-remove-${idx}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#E0E0E0] rounded-lg py-6 cursor-pointer hover:border-[#FF6B35] hover:bg-[#FFF8F3] transition-colors">
                <Paperclip size={18} className="text-[#666666]" />
                <span className="text-sm text-[#666666]">Нажмите чтобы добавить файлы</span>
                <input
                  type="file"
                  multiple
                  onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                  className="hidden"
                  data-testid="submit-files-input"
                />
              </label>
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <Button
                onClick={handleSubmit}
                className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white text-base font-semibold px-8 py-6"
                data-testid="submit-restaurant-task-btn"
              >
                <Send size={16} className="mr-2" /> Отправить обращение
              </Button>
            </div>
          </div>
        )}

        {step === 'submitting' && (
          <div className="flex flex-col items-center justify-center py-24 space-y-4" data-testid="submitting-state">
            <Loader2 className="animate-spin text-[#FF6B35]" size={48} />
            <p className="text-lg text-[#111111] font-medium">ИИ ассистент анализирует ваше обращение...</p>
            <p className="text-sm text-[#666666]">Ищем похожие задачи и разбираем описание</p>
          </div>
        )}

        {step === 'duplicates' && (
          <div className="space-y-6" data-testid="duplicates-screen">
            <div className="bg-white rounded-2xl border-2 border-[#F59E0B] p-6 sm:p-8 text-center space-y-3 shadow-sm">
              <div className="w-14 h-14 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] flex items-center justify-center mx-auto">
                <AlertCircle size={30} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#111111]">Это уже в работе</h2>
              <p className="text-sm text-[#666666] max-w-md mx-auto">
                ИИ ассистент нашёл похожие задачи, которые уже переданы команде. Проверьте — возможно, ваша проблема уже решается.
              </p>
            </div>

            <div className="space-y-3">
              {duplicates.map((t) => (
                <div key={t.id} className="bg-white rounded-xl border border-[#E0E0E0] p-5 shadow-sm" data-testid={`duplicate-task-${t.id}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: `${STATUS_COLORS[t.status] || '#64748B'}15`, color: STATUS_COLORS[t.status] || '#64748B' }}
                      data-testid={`duplicate-status-${t.id}`}
                    >
                      <Clock size={11} /> {t.status_label}
                    </span>
                    {t.module_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[#666666]">{t.module_name}</span>
                    )}
                    {t.priority && (
                      <span className="text-xs text-[#666666]">Приоритет: {PRIORITY_LABELS_RU[t.priority] || t.priority}</span>
                    )}
                  </div>
                  <h3 className="text-base font-semibold text-[#111111] mb-2" data-testid={`duplicate-title-${t.id}`}>{t.title}</h3>
                  {t.description && (
                    <p className="text-sm text-[#666666] whitespace-pre-line line-clamp-3">{t.description}</p>
                  )}
                  {t.assignee_name && (
                    <p className="text-xs text-[#999999] mt-2 flex items-center gap-1">
                      <UserIcon size={11} /> Ответственный: {t.assignee_name}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-[#F5F5F5] rounded-xl p-5 space-y-3">
              <p className="text-sm text-[#111111] font-medium">Что делать?</p>
              <p className="text-xs text-[#666666]">
                Если ваша проблема отличается от найденных выше — вы можете всё равно создать новое обращение. Иначе — команда уже занимается этим вопросом.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  onClick={resetForm}
                  variant="outline"
                  className="w-full sm:w-auto"
                  data-testid="duplicates-close-btn"
                >
                  Спасибо, это то что нужно
                </Button>
                <Button
                  onClick={handleForceSubmit}
                  className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white w-full sm:w-auto"
                  data-testid="duplicates-force-submit-btn"
                >
                  <Send size={14} className="mr-2" /> Всё равно отправить
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="bg-white rounded-2xl border border-[#E0E0E0] p-8 sm:p-10 text-center space-y-6 shadow-sm" data-testid="submit-success">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
              <CheckCircle2 size={40} />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-[#111111]">Спасибо! Обращение отправлено</h2>
              <p className="text-sm text-[#666666]">Команда разработки уже получила уведомление в Telegram и приступит к работе.</p>
            </div>
            {submittedTask?.title && (
              <div className="bg-[#F5F5F5] rounded-lg p-4 text-left">
                <p className="text-xs text-[#666666] mb-1">Ваше обращение:</p>
                <p className="text-sm font-medium text-[#111111]" data-testid="submitted-title">{submittedTask.title}</p>
              </div>
            )}
            <Button
              onClick={resetForm}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
              data-testid="submit-another-btn"
            >
              Отправить ещё одно обращение
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
